import { ref, get, set, update, remove, push, onValue } from 'firebase/database';
import { db as defaultDb } from '../firebase.js';

// Allow database instance to be overridden for testing
let db = defaultDb;

/**
 * Set database instance (for testing purposes)
 * @param {Database} dbInstance - Firebase database instance
 */
export function setDatabaseInstance(dbInstance) {
  db = dbInstance;
}

/**
 * Reset database instance to default
 */
export function resetDatabaseInstance() {
  db = defaultDb;
}

/**
 * User Data Operations
 */

/**
 * Sync user data to database - creates new user or updates existing user
 * IMPORTANT: PII (email, displayName, photoURL) is NEVER stored in Firebase.
 * Only uid and admin status are persisted server-side.
 * @param {Object} user - Firebase user object
 * @returns {Promise<void>}
 */
export async function syncUserData(user) {
  try {
    if (!user || !user.uid) {
      throw new Error('Invalid user object: uid is required');
    }

    const userRef = ref(db, `users/${user.uid}`);

    // Check if user exists
    const snapshot = await get(userRef);

    if (snapshot.exists()) {
      // Update existing user - preserve isAdmin field, do NOT update PII
      // PII is stored client-side only via Redux/Context
      await update(userRef, {
        lastLoginAt: Date.now()
      });
    } else {
      // Create new user with default isAdmin = false
      // NO PII stored - only uid and admin status
      await set(userRef, {
        uid: user.uid,
        isAdmin: false,
        createdAt: Date.now(),
        lastLoginAt: Date.now(),
        downloadCount: 0,
        downloadHistory: {}
      });
    }
  } catch (error) {
    console.error('Error syncing user data:', error);
    throw new Error(`Failed to sync user data: ${error.message}`);
  }
}

/**
 * Get user data from database
 * @param {string} uid - User ID
 * @returns {Promise<Object|null>} User record or null if not found
 */
export async function getUserData(uid) {
  try {
    if (!uid) {
      throw new Error('User ID is required');
    }

    const userRef = ref(db, `users/${uid}`);
    const snapshot = await get(userRef);

    if (snapshot.exists()) {
      return snapshot.val();
    }

    return null;
  } catch (error) {
    console.error('Error getting user data:', error);
    throw new Error(`Failed to get user data: ${error.message}`);
  }
}

/**
 * Update user admin status
 * @param {string} uid - User ID
 * @param {boolean} isAdmin - Admin status
 * @returns {Promise<void>}
 */
export async function updateUserAdmin(uid, isAdmin) {
  try {
    if (!uid) {
      throw new Error('User ID is required');
    }

    if (typeof isAdmin !== 'boolean') {
      throw new Error('isAdmin must be a boolean value');
    }

    const userRef = ref(db, `users/${uid}`);

    // Check if user exists
    const snapshot = await get(userRef);
    if (!snapshot.exists()) {
      throw new Error(`User ${uid} does not exist`);
    }

    await update(userRef, { isAdmin });
  } catch (error) {
    console.error('Error updating user admin status:', error);
    throw new Error(`Failed to update user admin status: ${error.message}`);
  }
}

/**
 * Model Data Operations
 */

/**
 * Get all models from database
 * @returns {Promise<Array>} Array of model records
 */
export async function getAllModels() {
  try {
    const modelsRef = ref(db, 'models');
    const snapshot = await get(modelsRef);

    if (snapshot.exists()) {
      const modelsData = snapshot.val();
      // Convert object to array with model_id included
      return Object.entries(modelsData).map(([id, data]) => ({
        model_id: id,
        ...data
      }));
    }

    return [];
  } catch (error) {
    console.error('Error getting all models:', error);
    throw new Error(`Failed to get all models: ${error.message}`);
  }
}

/**
 * Get a specific model by ID
 * @param {string} modelId - Model ID
 * @returns {Promise<Object|null>} Model record or null if not found
 */
export async function getModelById(modelId) {
  try {
    if (!modelId) {
      throw new Error('Model ID is required');
    }

    const modelRef = ref(db, `models/${modelId}`);
    const snapshot = await get(modelRef);

    if (snapshot.exists()) {
      return {
        model_id: modelId,
        ...snapshot.val()
      };
    }

    return null;
  } catch (error) {
    console.error('Error getting model by ID:', error);
    throw new Error(`Failed to get model: ${error.message}`);
  }
}

/**
 * Create a new model in the database
 * @param {Object} modelData - Model data (without model_id)
 * @returns {Promise<string>} The generated model ID
 */
export async function createModel(modelData) {
  try {
    if (!modelData) {
      throw new Error('Model data is required');
    }

    // Generate unique ID
    const modelsRef = ref(db, 'models');
    const newModelRef = push(modelsRef);
    const modelId = newModelRef.key;

    // Set timestamps
    const timestamp = Date.now();
    const modelRecord = {
      ...modelData,
      model_created_at: timestamp,
      model_updated_at: timestamp,
      downloads: modelData.downloads || 0,
      featured: modelData.featured || false
    };

    await set(newModelRef, modelRecord);

    return modelId;
  } catch (error) {
    console.error('Error creating model:', error);
    throw new Error(`Failed to create model: ${error.message}`);
  }
}

/**
 * Update an existing model
 * @param {string} modelId - Model ID
 * @param {Object} updates - Fields to update
 * @returns {Promise<void>}
 */
export async function updateModel(modelId, updates) {
  try {
    if (!modelId) {
      throw new Error('Model ID is required');
    }

    if (!updates || Object.keys(updates).length === 0) {
      throw new Error('Update data is required');
    }

    const modelRef = ref(db, `models/${modelId}`);

    // Check if model exists
    const snapshot = await get(modelRef);
    if (!snapshot.exists()) {
      throw new Error(`Model ${modelId} does not exist`);
    }

    // Add updated timestamp
    const updateData = {
      ...updates,
      model_updated_at: Date.now()
    };

    await update(modelRef, updateData);
  } catch (error) {
    console.error('Error updating model:', error);
    throw new Error(`Failed to update model: ${error.message}`);
  }
}

/**
 * Delete a model from the database
 * @param {string} modelId - Model ID
 * @returns {Promise<void>}
 */
export async function deleteModel(modelId) {
  try {
    if (!modelId) {
      throw new Error('Model ID is required');
    }

    const modelRef = ref(db, `models/${modelId}`);

    // Check if model exists
    const snapshot = await get(modelRef);
    if (!snapshot.exists()) {
      throw new Error(`Model ${modelId} does not exist`);
    }

    await remove(modelRef);
  } catch (error) {
    console.error('Error deleting model:', error);
    throw new Error(`Failed to delete model: ${error.message}`);
  }
}

/**
 * Real-time Listener Functions
 */

/**
 * Listen to all models in real-time
 * @param {Function} callback - Callback function to invoke when data changes
 * @returns {Function} Unsubscribe function
 */
export function listenToModels(callback) {
  try {
    if (typeof callback !== 'function') {
      throw new Error('Callback must be a function');
    }

    const modelsRef = ref(db, 'models');

    const unsubscribe = onValue(modelsRef, (snapshot) => {
      try {
        if (snapshot.exists()) {
          const modelsData = snapshot.val();
          // Convert object to array with model_id included
          const modelsArray = Object.entries(modelsData).map(([id, data]) => ({
            model_id: id,
            ...data
          }));
          callback(modelsArray);
        } else {
          callback([]);
        }
      } catch (error) {
        console.error('Error in models listener callback:', error);
      }
    }, (error) => {
      console.error('Error listening to models:', error);
      // Attempt to re-establish connection after a delay
      setTimeout(() => {
        listenToModels(callback);
      }, 5000);
    });

    return unsubscribe;
  } catch (error) {
    console.error('Error setting up models listener:', error);
    throw new Error(`Failed to set up models listener: ${error.message}`);
  }
}

/**
 * Listen to a specific user in real-time
 * @param {string} uid - User ID
 * @param {Function} callback - Callback function to invoke when data changes
 * @returns {Function} Unsubscribe function
 */
export function listenToUser(uid, callback) {
  try {
    if (!uid) {
      throw new Error('User ID is required');
    }

    if (typeof callback !== 'function') {
      throw new Error('Callback must be a function');
    }

    const userRef = ref(db, `users/${uid}`);

    const unsubscribe = onValue(userRef, (snapshot) => {
      try {
        if (snapshot.exists()) {
          callback(snapshot.val());
        } else {
          callback(null);
        }
      } catch (error) {
        console.error('Error in user listener callback:', error);
      }
    }, (error) => {
      console.error('Error listening to user:', error);
      // Attempt to re-establish connection after a delay
      setTimeout(() => {
        listenToUser(uid, callback);
      }, 5000);
    });

    return unsubscribe;
  } catch (error) {
    console.error('Error setting up user listener:', error);
    throw new Error(`Failed to set up user listener: ${error.message}`);
  }
}

/**
 * Increment download count for a model
 * @param {string} modelId - Model ID
 * @returns {Promise<void>}
 */
export async function incrementDownloadCount(modelId) {
  try {
    if (!modelId) {
      throw new Error('Model ID is required');
    }

    const modelRef = ref(db, `models/${modelId}`);

    // Get current model data
    const snapshot = await get(modelRef);
    if (!snapshot.exists()) {
      throw new Error(`Model ${modelId} does not exist`);
    }

    const currentData = snapshot.val();
    const currentDownloads = currentData.downloads || 0;

    // Increment download count
    await update(modelRef, {
      downloads: currentDownloads + 1,
      model_updated_at: Date.now()
    });
  } catch (error) {
    console.error('Error incrementing download count:', error);
    throw new Error(`Failed to increment download count: ${error.message}`);
  }
}


/**
 * Increment download count for a model by format
 * Tracks both total downloads and per-format downloads
 * @param {string} modelId - Model ID
 * @param {string} format - File format (dwg, ifc, rfa, rvt, xkt)
 * @returns {Promise<void>}
 */
export async function incrementDownloadCountByFormat(modelId, format) {
  try {
    if (!modelId) {
      throw new Error('Model ID is required');
    }

    if (!format) {
      throw new Error('Format is required');
    }

    const validFormats = ['dwg', 'ifc', 'rfa', 'rvt', 'xkt'];
    if (!validFormats.includes(format.toLowerCase())) {
      throw new Error(`Invalid format: ${format}. Must be one of: ${validFormats.join(', ')}`);
    }

    const modelRef = ref(db, `models/${modelId}`);

    // Get current model data
    const snapshot = await get(modelRef);
    if (!snapshot.exists()) {
      throw new Error(`Model ${modelId} does not exist`);
    }

    const currentData = snapshot.val();
    const currentDownloads = currentData.downloads || 0;
    const currentDownloadsByFormat = currentData.downloads_by_format || {};
    const currentFormatCount = currentDownloadsByFormat[format.toLowerCase()] || 0;

    // Increment both total and format-specific counts
    await update(modelRef, {
      downloads: currentDownloads + 1,
      [`downloads_by_format/${format.toLowerCase()}`]: currentFormatCount + 1,
      model_updated_at: Date.now()
    });
  } catch (error) {
    console.error('Error incrementing download count by format:', error);
    throw new Error(`Failed to increment download count: ${error.message}`);
  }
}

/**
 * Track user download - records download in user's download history
 * @param {string} uid - User ID
 * @param {string} modelId - Model ID
 * @param {string} format - File format
 * @returns {Promise<void>}
 */
export async function trackUserDownload(uid, modelId, format) {
  try {
    if (!uid) {
      throw new Error('User ID is required');
    }

    if (!modelId) {
      throw new Error('Model ID is required');
    }

    if (!format) {
      throw new Error('Format is required');
    }

    const userRef = ref(db, `users/${uid}`);
    const timestamp = Date.now();
    const downloadKey = `${modelId}_${format}_${timestamp}`;

    // Update user's download history and count
    await update(userRef, {
      [`downloadHistory/${downloadKey}`]: {
        modelId,
        format,
        downloadedAt: timestamp
      },
      downloadCount: (await get(userRef)).val()?.downloadCount || 0 + 1,
      lastDownloadAt: timestamp
    });
  } catch (error) {
    console.error('Error tracking user download:', error);
    throw new Error(`Failed to track download: ${error.message}`);
  }
}

/**
 * Get user's download history
 * @param {string} uid - User ID
 * @returns {Promise<Array>} Array of download records
 */
export async function getUserDownloadHistory(uid) {
  try {
    if (!uid) {
      throw new Error('User ID is required');
    }

    const userRef = ref(db, `users/${uid}/downloadHistory`);
    const snapshot = await get(userRef);

    if (snapshot.exists()) {
      const history = snapshot.val();
      return Object.entries(history).map(([key, data]) => ({
        key,
        ...data
      }));
    }

    return [];
  } catch (error) {
    console.error('Error getting user download history:', error);
    throw new Error(`Failed to get download history: ${error.message}`);
  }
}

/**
 * Check if user has already downloaded a specific model/format combination
 * @param {string} uid - User ID
 * @param {string} modelId - Model ID
 * @param {string} format - File format
 * @returns {Promise<boolean>} True if user has downloaded this model/format
 */
export async function hasUserDownloaded(uid, modelId, format) {
  try {
    if (!uid || !modelId || !format) {
      throw new Error('User ID, Model ID, and format are required');
    }

    const history = await getUserDownloadHistory(uid);
    return history.some(
      record => record.modelId === modelId && record.format === format
    );
  } catch (error) {
    console.error('Error checking download status:', error);
    throw new Error(`Failed to check download status: ${error.message}`);
  }
}
